/**
 * Running a space: the Group Admin's settings and the Hosts' queue.
 * `docs/plans/community.md` mocks D, E, I and J.
 *
 * Every refusal is the server's — these screens only offer what the role
 * returned by `/community/{org}` allows, and the server checks again.
 */

import { useCallback, useEffect, useState } from "react";

import {
  banUser,
  decide,
  fetchMembers,
  fetchQueue,
  reorderTopics,
  saveSpaceSettings,
  saveTopic,
  setHost,
  type Post,
  type Report,
  type Space,
  type Topic,
} from "../../api/community";
import { relTime } from "../../lib/community";
import { Badge, Btn, CARD, ErrorLine, META, Pill, PostText, TopicDot, type OpenActions } from "./CommunityParts";

const COLOURS = ["blue", "green", "amber", "violet", "red", "teal", "pink", "slate"];

type Tab = "topics" | "hosts" | "rules" | "open";

export function SpaceSettings({
  space,
  onChanged,
}: {
  space: Space;
  onChanged: (space: Space) => void;
}) {
  const admin = space.role === "admin";
  const [tab, setTab] = useState<Tab>("topics");
  const [error, setError] = useState<string | null>(null);
  const guard = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "that did not save");
    }
  };
  const save = (patch: Partial<Space>) => guard(async () => onChanged(await saveSpaceSettings(space.orgKey, patch)));

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        <Pill on={tab === "topics"} onClick={() => setTab("topics")}>Topics</Pill>
        {admin && <Pill on={tab === "hosts"} onClick={() => setTab("hosts")}>Hosts ({space.hosts.length})</Pill>}
        {admin && <Pill on={tab === "rules"} onClick={() => setTab("rules")}>Rules</Pill>}
        {admin && <Pill on={tab === "open"} onClick={() => setTab("open")}>Open House</Pill>}
      </div>
      {admin && (
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, marginBottom: 8 }}>
          <input type="checkbox" checked={space.enabled} onChange={(e) => void save({ enabled: e.target.checked })} />
          Community is on for {space.name}
          {!space.enabled && <span style={META}>— members see nothing until it is on</span>}
        </label>
      )}
      <ErrorLine error={error} />
      {tab === "topics" && <TopicsEditor space={space} onChanged={onChanged} guard={guard} />}
      {tab === "hosts" && admin && <HostsEditor space={space} guard={guard} onChanged={onChanged} />}
      {tab === "rules" && admin && <RulesEditor space={space} save={save} />}
      {tab === "open" && admin && <OpenHouseEditor space={space} save={save} onChanged={onChanged} guard={guard} />}
    </div>
  );
}

function TopicsEditor({
  space,
  onChanged,
  guard,
}: {
  space: Space;
  onChanged: (space: Space) => void;
  guard: (fn: () => Promise<void>) => Promise<void>;
}) {
  const topics = space.topics ?? [];
  const [name, setName] = useState("");
  const [colour, setColour] = useState("blue");
  const [policy, setPolicy] = useState<"members" | "hosts">("members");
  const [replies, setReplies] = useState<"everyone" | "hosts" | "off">("everyone");
  const [description, setDescription] = useState("");

  const replace = (topic: Topic) =>
    onChanged({ ...space, topics: topics.some((t) => t.id === topic.id) ? topics.map((t) => (t.id === topic.id ? topic : t)) : [...topics, topic] });

  const move = (index: number, by: -1 | 1) =>
    guard(async () => {
      const ids = topics.map((t) => t.id);
      const to = index + by;
      if (to < 0 || to >= ids.length) return;
      [ids[index], ids[to]] = [ids[to]!, ids[index]!];
      onChanged({ ...space, topics: await reorderTopics(space.orgKey, ids) });
    });

  return (
    <div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ ...META, textAlign: "left" }}>
            <th style={TH}></th>
            <th style={TH}>Topic</th>
            <th style={TH}>Who can post</th>
            <th style={TH}>Replies</th>
            <th style={TH}>Open House</th>
            <th style={TH}></th>
          </tr>
        </thead>
        <tbody>
          {topics.map((t, i) => (
            <tr key={t.id} style={{ opacity: t.archived ? 0.5 : 1 }}>
              <td style={TD}>
                <button type="button" style={ARROW} onClick={() => void move(i, -1)} title="Up">▲</button>
                <button type="button" style={ARROW} onClick={() => void move(i, 1)} title="Down">▼</button>
              </td>
              <td style={TD}>
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><TopicDot colour={t.colour} />{t.name}</span>
              </td>
              <td style={TD}>
                <select
                  value={t.postPolicy}
                  onChange={(e) => void guard(async () => replace(await saveTopic(space.orgKey, t.id, { postPolicy: e.target.value as Topic["postPolicy"] })))}
                  style={SELECT}
                >
                  <option value="members">Members</option>
                  <option value="hosts">Hosts only</option>
                </select>
              </td>
              <td style={TD}>
                <select
                  value={t.replyPolicy ?? "everyone"}
                  title="Who may reply to threads in this topic"
                  onChange={(e) => void guard(async () => replace(await saveTopic(space.orgKey, t.id, { replyPolicy: e.target.value as Topic["replyPolicy"] })))}
                  style={SELECT}
                >
                  <option value="everyone">Everyone</option>
                  <option value="hosts">Hosts only</option>
                  <option value="off">Off</option>
                </select>
              </td>
              <td style={TD}>
                <input
                  type="checkbox"
                  checked={t.isOpen}
                  title="Visible to Open House visitors"
                  onChange={(e) => void guard(async () => replace(await saveTopic(space.orgKey, t.id, { isOpen: e.target.checked })))}
                />
              </td>
              <td style={TD}>
                <Btn small onClick={() => void guard(async () => replace(await saveTopic(space.orgKey, t.id, { archived: !t.archived })))}>
                  {t.archived ? "Restore" : "Archive"}
                </Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ ...CARD, marginTop: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>New topic</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label style={FIELD}>
            Name
            <input value={name} maxLength={48} onChange={(e) => setName(e.target.value)} placeholder="Topic name (e.g. Welcome)" style={{ ...SELECT, width: 190 }} />
          </label>
          <label style={FIELD}>
            Colour
            <select value={colour} onChange={(e) => setColour(e.target.value)} style={SELECT}>
              {COLOURS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label style={FIELD}>
            Who can post
            <select value={policy} onChange={(e) => setPolicy(e.target.value as "members" | "hosts")} style={SELECT}>
              <option value="members">Members</option>
              <option value="hosts">Hosts only</option>
            </select>
          </label>
          <label style={FIELD}>
            Replies
            <select value={replies} onChange={(e) => setReplies(e.target.value as "everyone" | "hosts" | "off")} style={SELECT}>
              <option value="everyone">Everyone</option>
              <option value="hosts">Hosts only</option>
              <option value="off">Off</option>
            </select>
          </label>
          <Btn
            primary
            disabled={!name.trim()}
            onClick={() =>
              void guard(async () => {
                replace(await saveTopic(space.orgKey, null, { name, colour, postPolicy: policy, replyPolicy: replies, description }));
                setName("");
                setDescription("");
              })
            }
          >
            Add
          </Btn>
        </div>
        <label style={{ ...FIELD, display: "block", marginTop: 6 }}>
          Description
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown at the top of the topic" style={{ ...SELECT, width: "100%", boxSizing: "border-box" }} />
        </label>
      </div>
    </div>
  );
}

function HostsEditor({
  space,
  guard,
  onChanged,
}: {
  space: Space;
  guard: (fn: () => Promise<void>) => Promise<void>;
  onChanged: (space: Space) => void;
}) {
  const [members, setMembers] = useState<{ userKey: string; name: string; role: string; host: boolean }[]>([]);
  const [warn, setWarn] = useState(false);
  const load = useCallback(() => guard(async () => setMembers(await fetchMembers(space.orgKey))), [guard, space.orgKey]);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space.orgKey]);
  return (
    <div>
      <div style={META}>Hosts create and run topics, approve Open House posts and moderate. They get no other power in the group.</div>
      {warn && <div style={{ ...META, color: "var(--qf-warn)" }}>More than 5 hosts — that is a lot of people deciding what stays up.</div>}
      <div style={{ marginTop: 6 }}>
        {members.filter((m) => m.role !== "admin").map((m) => (
          <label key={m.userKey} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, padding: "2px 0" }}>
            <input
              type="checkbox"
              checked={m.host}
              onChange={(e) =>
                void guard(async () => {
                  const out = await setHost(space.orgKey, m.userKey, e.target.checked);
                  setWarn(out.warn);
                  setMembers((was) => was.map((x) => (x.userKey === m.userKey ? { ...x, host: e.target.checked } : x)));
                  onChanged({ ...space, hosts: e.target.checked ? [...space.hosts, { name: m.name, userKey: m.userKey }] : space.hosts.filter((h) => h.userKey !== m.userKey) });
                })
              }
            />
            {m.name}
          </label>
        ))}
        {members.length === 0 && <div style={META}>Loading members…</div>}
      </div>
    </div>
  );
}

function RulesEditor({ space, save }: { space: Space; save: (patch: Partial<Space>) => Promise<void> }) {
  const [rules, setRules] = useState(space.rules);
  const [limit, setLimit] = useState(space.rateLimit);
  return (
    <div>
      <label style={{ display: "block", ...META }}>
        Rules shown beside the topics
        <textarea value={rules} onChange={(e) => setRules(e.target.value)} rows={5} style={{ ...SELECT, width: "100%", boxSizing: "border-box", fontFamily: "inherit" }} />
      </label>
      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, fontSize: 13 }}>
        Posts per member per hour
        <input type="number" min={1} max={60} value={limit} onChange={(e) => setLimit(Number(e.target.value))} style={{ ...SELECT, width: 70 }} />
        <span style={META}>new groups start at 10 · at most 60 · hosts and admins are not limited · applies to visitors too</span>
      </label>
      <div style={{ marginTop: 8 }}>
        <Btn primary onClick={() => void save({ rules, rateLimit: limit })}>Save</Btn>
      </div>
    </div>
  );
}

function OpenHouseEditor({
  space,
  save,
  onChanged,
  guard,
}: {
  space: Space;
  save: (patch: Partial<Space>) => Promise<void>;
  onChanged: (space: Space) => void;
  guard: (fn: () => Promise<void>) => Promise<void>;
}) {
  const [description, setDescription] = useState(space.openDescription);
  const topics = space.topics ?? [];
  return (
    <div>
      <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
        <input type="checkbox" checked={space.openHouse} onChange={(e) => void save({ openHouse: e.target.checked })} />
        Open House — any app user can join and post; their posts, comments and edits wait for a host
      </label>
      <div style={{ ...META, marginTop: 8 }}>Topics open to visitors</div>
      {topics.filter((t) => !t.archived).map((t) => (
        <label key={t.id} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={t.isOpen}
            onChange={(e) =>
              void guard(async () => {
                const saved = await saveTopic(space.orgKey, t.id, { isOpen: e.target.checked });
                onChanged({ ...space, topics: topics.map((x) => (x.id === saved.id ? saved : x)) });
              })
            }
          />
          <TopicDot colour={t.colour} /> {t.name}
        </label>
      ))}
      <label style={{ display: "block", ...META, marginTop: 8 }}>
        Description shown on Discover
        <input value={description} maxLength={600} onChange={(e) => setDescription(e.target.value)} style={{ ...SELECT, width: "100%", boxSizing: "border-box" }} />
      </label>
      <div style={{ marginTop: 8 }}>
        <Btn primary onClick={() => void save({ openDescription: description })}>Save</Btn>
      </div>
    </div>
  );
}

// ── the queue ────────────────────────────────────────────────────────────────

export function ModerationQueue({
  orgKey,
  actions,
  onOpenThread,
  onCounts,
}: {
  orgKey: string;
  actions: OpenActions;
  onOpenThread: (threadId: string) => void;
  onCounts?: () => void;
}) {
  const [approvals, setApprovals] = useState<Post[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [tab, setTab] = useState<"approvals" | "reports">("approvals");
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const q = await fetchQueue(orgKey);
      setApprovals(q.approvals);
      setReports(q.reports);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "the queue did not load");
    }
  }, [orgKey]);
  useEffect(() => {
    void load();
  }, [load]);

  const act = async (post: Post, action: Parameters<typeof decide>[2], reason = "") => {
    try {
      await decide(orgKey, post.id, action, reason);
      await load();
      onCounts?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "that did not work");
    }
  };
  const banAuthor = async (post: Post) => {
    if (!post.authorKey) return;
    try {
      await banUser(orgKey, post.authorKey, true);
      await load();
      onCounts?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "that did not work");
    }
  };

  const shown = (p: Post) => (p.kindOfWait === "edit" && p.pending ? { ...p, title: p.pending.title ?? p.title, body: p.pending.body ?? p.body, embeds: p.pending.embeds } : p);

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <Pill on={tab === "approvals"} onClick={() => setTab("approvals")}>Approvals ({approvals.length})</Pill>
        <Pill on={tab === "reports"} onClick={() => setTab("reports")}>Reports ({reports.length})</Pill>
        <span style={{ flex: 1 }} />
        <Btn small onClick={() => void load()}>Refresh</Btn>
      </div>
      <ErrorLine error={error} />
      {tab === "approvals" &&
        (approvals.length === 0 ? (
          <div style={META}>Nothing is waiting. Pending posts expire after 7 days.</div>
        ) : (
          approvals.map((raw) => {
            const p = shown(raw);
            return (
              <div key={p.id} style={CARD}>
                <div style={META}>
                  {p.kindOfWait === "edit" ? <Badge kind="edit" /> : <Badge kind="pending" />}
                  {p.depth === 0 ? "thread" : "comment"} · <b>{p.author}</b> · {relTime(p.createdAt)} ·{" "}
                  {p.history ? `${p.history.approved} approved · ${p.history.rejected} rejected` : ""}
                </div>
                {p.title && <div style={{ fontWeight: 600, margin: "4px 0" }}>{p.title}</div>}
                <PostText text={p.body} onSymbol={actions.onOpenSymbol} />
                {p.embeds.length > 0 && <div style={META}>{p.embeds.length} attachment{p.embeds.length > 1 ? "s" : ""}</div>}
                <input
                  placeholder="Reason, if rejecting (the author sees it)"
                  value={reasons[p.id] ?? ""}
                  onChange={(e) => setReasons((was) => ({ ...was, [p.id]: e.target.value }))}
                  style={{ ...SELECT, width: "100%", boxSizing: "border-box", margin: "6px 0" }}
                />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Btn small primary onClick={() => void act(raw, "approve")}>Approve</Btn>
                  <Btn small onClick={() => void act(raw, "reject", reasons[p.id] ?? "")}>Reject</Btn>
                  {raw.authorKey && <Btn small danger onClick={() => void banAuthor(raw)}>Ban author</Btn>}
                  {raw.threadId && <Btn small onClick={() => onOpenThread(raw.threadId!)}>Open thread</Btn>}
                </div>
              </div>
            );
          })
        ))}
      {tab === "reports" &&
        (reports.length === 0 ? (
          <div style={META}>No open reports.</div>
        ) : (
          reports.map((r) => (
            <div key={r.id} style={CARD}>
              <div style={META}>
                Reported <b>{r.reason}</b> by {r.reporter} · {relTime(r.createdAt)}
                {r.note ? ` — “${r.note}”` : ""}
              </div>
              <div style={{ margin: "4px 0", fontSize: 12 }}>
                <b>{r.post.author}</b>: {r.post.title || r.post.body.slice(0, 200)}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Btn small danger onClick={() => void act(r.post, "remove")}>Remove</Btn>
                {r.post.depth === 0 && <Btn small onClick={() => void act(r.post, "lock")}>Lock</Btn>}
                {r.post.authorKey && <Btn small danger onClick={() => void banAuthor(r.post)}>Ban author</Btn>}
                <Btn small onClick={() => void act(r.post, "dismiss")}>Dismiss</Btn>
                {r.post.threadId && <Btn small onClick={() => onOpenThread(r.post.threadId!)}>Open thread</Btn>}
              </div>
            </div>
          ))
        ))}
      <div style={{ ...META, marginTop: 8 }}>Removed posts show as “removed by a host” and keep their replies.</div>
    </div>
  );
}

const TH: React.CSSProperties = { padding: "4px 6px", borderBottom: "1px solid var(--qf-border)", fontWeight: 600 };
const TD: React.CSSProperties = { padding: "4px 6px", borderBottom: "1px solid var(--qf-border)", verticalAlign: "middle" };
const ARROW: React.CSSProperties = { border: 0, background: "none", color: "var(--qf-text-secondary)", cursor: "pointer", fontSize: 9, padding: "0 2px" };
const SELECT: React.CSSProperties = {
  fontSize: 12,
  padding: "3px 6px",
  color: "var(--qf-text)",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
};

/** A small label over each new-topic field — the name box looked like a filled-in value without one. */
const FIELD: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  fontSize: 11,
  color: "var(--qf-text-secondary)",
};
